import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { useQueue } from '@/components/QueueContext';
import { useConfirmation } from '@/components/shared/ZCard';
import {
  Trash2, Paperclip, Download, X, Check,
  Loader2, Link as LinkIcon, Upload, FileText, ChevronDown, ChevronLeft,
  Pencil, Sparkles
} from 'lucide-react';
import OverflowMenu from '@/components/shared/OverflowMenu';
import ZTabs from '@/components/shared/ZTabs';
import ZEmptyState from '@/components/shared/ZEmptyState';
import ZLoadingBar from '@/components/shared/ZLoadingBar';
import Logotype from '@/components/shared/Logotype';

const ACCENT = '#C0A96A';
const BG = '#0a0a0f';
const CARD = 'rgba(255,255,255,0.03)';
const BORDER = 'rgba(255,255,255,0.06)';
const TEXT = '#f0f0f5';
const TEXT2 = '#b0b4c0';
const TEXT3 = '#555555';

const CATEGORY_LABELS = {
  person: 'Person',
  business: 'Business',
  physical: 'Physical Asset',
  legal: 'Legal',
  credential: 'Credential',
};

const CATEGORY_FIELDS = {
  person: ['full_name','id_number','passport_number','passport_expiry','date_of_birth','tax_number','medical_aid_number','medical_aid_plan','bank_account_number','bank_name','branch_code','contact_number','email','physical_address','nationality','gender','place_of_birth','us_visa','notes'],
  business: ['registered_name','trading_name','registration_number','vat_number','registered_address','directors','bank_account_number','bank_name','branch_code','tax_number','paye_number','uif_number','industry','key_contacts','website','notes'],
  physical: ['description','make','model','year','vin_number','registration_number','address','purchase_date','purchase_price_zar','current_value_zar','finance_provider','finance_account_number','insurer','policy_number','serial_number','notes'],
  legal: ['document_type','parties_involved','date_signed','expiry_date','status','jurisdiction','notes'],
  credential: ['credential_type','provider','account_reference','login_url','expiry_date','linked_asset_id','notes'],
};

const TABS = ['all','person','business','physical','legal'];

function fieldLabel(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function CategoryPill({ cat }) {
  const colors = { person: '#6B8FD4', business: ACCENT, physical: '#10B981', legal: '#E85D24', credential: '#9B6DD4' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 10, letterSpacing: '0.10em', textTransform: 'uppercase',
      fontFamily: "'JetBrains Mono', monospace",
      background: `${colors[cat] || ACCENT}22`,
      color: colors[cat] || ACCENT,
      border: `1px solid ${colors[cat] || ACCENT}44`,
    }}>
      {CATEGORY_LABELS[cat] || cat}
    </span>
  );
}

function FieldRow({ fieldKey, value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');
  const inputRef = useRef(null);
  const isNotes = fieldKey === 'notes';

  useEffect(() => { setVal(value || ''); }, [value]);

  const startEdit = () => { setEditing(true); setTimeout(() => inputRef.current?.focus(), 50); };

  const handleBlur = () => {
    setEditing(false);
    if (val !== (value || '')) onSave(fieldKey, val);
  };

  return (
    <div style={{ display: 'flex', alignItems: isNotes ? 'flex-start' : 'center', padding: '10px 0', borderBottom: `0.5px solid ${BORDER}` }}>
      <span style={{ width: 200, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: TEXT2, letterSpacing: '0.06em', flexShrink: 0, paddingTop: isNotes ? 4 : 0 }}>
        {fieldLabel(fieldKey)}
      </span>
      {editing ? (
        isNotes ? (
          <textarea
            ref={inputRef}
            value={val}
            onChange={e => setVal(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={e => { if (e.key === 'Escape') { setVal(value || ''); setEditing(false); } }}
            rows={4}
            style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: `1px solid ${ACCENT}66`, borderRadius: 6, padding: '6px 10px', color: TEXT, fontSize: 14, fontFamily: 'inherit', outline: 'none', resize: 'vertical', lineHeight: 1.5 }}
          />
        ) : (
          <input
            ref={inputRef}
            value={val}
            onChange={e => setVal(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={e => { if (e.key === 'Enter') { e.target.blur(); } if (e.key === 'Escape') { setVal(value || ''); setEditing(false); } }}
            style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: `1px solid ${ACCENT}66`, borderRadius: 6, padding: '6px 10px', color: TEXT, fontSize: 14, fontFamily: 'inherit', outline: 'none' }}
          />
        )
      ) : (
        <span
          onClick={startEdit}
          style={{ flex: 1, fontSize: 14, color: value ? TEXT : TEXT3, cursor: 'text', minHeight: 22, fontStyle: value ? 'normal' : 'italic', whiteSpace: isNotes ? 'pre-wrap' : 'normal' }}
        >
          {value || '—'}
        </span>
      )}
    </div>
  );
}

export default function ArxActa() {
  const { count: queueCount, openQueue } = useQueue();
  const feedback = useConfirmation();
  const [assets, setAssets] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [tabCounts, setTabCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [assetLoading, setAssetLoading] = useState(false);

  // Ingestion modal (FAB)
  const [showIngestion, setShowIngestion] = useState(false);
  const [ingestText, setIngestText] = useState('');
  const [ingestFiles, setIngestFiles] = useState([]); // {file, name, type, preview}
  const [ingestStep, setIngestStep] = useState('input'); // input | processing | review
  const [ingestProposals, setIngestProposals] = useState([]);
  const [ingestApplying, setIngestApplying] = useState(false);



  // New asset modal
  const [showNewAsset, setShowNewAsset] = useState(false);
  const [newAssetName, setNewAssetName] = useState('');
  const [newAssetCategory, setNewAssetCategory] = useState('person');
  const [newAssetTags, setNewAssetTags] = useState('');
  const [creating, setCreating] = useState(false);

  // Documents

  // Extract
  const [extractDocId, setExtractDocId] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractedFields, setExtractedFields] = useState(null);
  const [selectedExtractFields, setSelectedExtractFields] = useState({});

  // PDF fill
  const [showPdfFill, setShowPdfFill] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfFillAssets, setPdfFillAssets] = useState([]);
  const [pdfFillInstructions, setPdfFillInstructions] = useState('');
  const [pdfFilling, setPdfFilling] = useState(false);
  const [filledPdfUrl, setFilledPdfUrl] = useState(null);
  const pdfInputRef = useRef(null);

  // Link assets
  const [showLinkPicker, setShowLinkPicker] = useState(false);

  // Floating composer
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerText, setComposerText] = useState('');
  const [composerFiles, setComposerFiles] = useState([]);
  const [composerDragging, setComposerDragging] = useState(false);
  const [composerSending, setComposerSending] = useState(false);
  const [composerKeyboardOffset, setComposerKeyboardOffset] = useState(0);
  const composerInputRef = useRef(null);
  const composerFileRef = useRef(null);

  // Mobile layout
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [mobileView, setMobileView] = useState('list'); // 'list' | 'detail'

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── File helpers ─────────────────────────────────────────────────────────────
  const addFiles = useCallback((fileList, setter) => {
    Array.from(fileList).forEach(file => {
      const isImage = file.type.startsWith('image/');
      const isPdf = file.type === 'application/pdf';
      const fType = isImage ? 'image' : isPdf ? 'pdf' : 'other';
      if (isImage) {
        const reader = new FileReader();
        reader.onload = e => setter(prev => [...prev, { file, name: file.name, type: fType, preview: e.target.result }]);
        reader.readAsDataURL(file);
      } else {
        setter(prev => [...prev, { file, name: file.name, type: fType, preview: null }]);
      }
    });
  }, []);
  const addIngestFiles = useCallback((fileList) => addFiles(fileList, setIngestFiles), [addFiles]);

  function closeIngestion() {
    setShowIngestion(false);
    setIngestText('');
    setIngestFiles([]);
    setIngestStep('input');
    setIngestProposals([]);
  }

  // ── Composer helpers ────────────────────────────────────────────────────────
  function openComposer() {
    setComposerOpen(true);
    setTimeout(() => composerInputRef.current?.focus(), 100);
  }
  function closeComposer() {
    setComposerOpen(false);
    setComposerText('');
    setComposerFiles([]);
  }

  // Keyboard tracking for composer (mobile)
  useEffect(() => {
    if (!composerOpen) { setComposerKeyboardOffset(0); return; }
    const update = () => {
      const vv = window.visualViewport;
      if (vv) setComposerKeyboardOffset(Math.max(0, window.innerHeight - vv.height - (vv.offsetTop || 0)));
    };
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    update();
    return () => {
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
    };
  }, [composerOpen]);

  async function submitIngest() {
    if (!ingestText.trim() && ingestFiles.length === 0) return;
    setIngestStep('processing');
    try {
      const form = new FormData();
      form.append('text', ingestText.trim());
      ingestFiles.forEach(f => form.append('files', f.file));
      const { data } = await api.post('/arx/ingest', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setIngestProposals(data.proposals || []);
      setIngestStep('review');
    } catch {
      feedback.fail();
      setIngestStep('input');
    }
  }

  async function acceptIngest() {
    setIngestApplying(true);
    let created = 0;
    try {
      for (const p of ingestProposals) {
        if (!p._discard) {
          let assetId = p.asset_id;
          if (p.action === 'create_new') {
            const { data: newAsset } = await api.post('/arx/assets', {
              name: p.asset_name, category: p.category, fields: p.fields, tags: [],
            });
            assetId = newAsset.id;
          } else if (assetId) {
            // Merge fields into existing asset
            const existing = assets.find(a => a.id === assetId);
            const merged = { ...(existing?.fields || {}), ...p.fields };
            await api.put(`/arx/assets/${assetId}`, { fields: merged });
          }
          // Attach the document if we have a URL
          if (assetId && p.cloudinary_url) {
            await api.post(`/arx/assets/${assetId}/attach-document`, {
              file_url: p.cloudinary_url,
              filename: p.doc_filename,
              file_type: p.doc_file_type,
            });
          }
          created++;
        }
      }
      await fetchAssets();
      feedback.confirm();
      closeIngestion();
    } catch (e) {
      feedback.fail();
    } finally {
      setIngestApplying(false);
    }
  }

  function updateProposal(idx, patch) {
    setIngestProposals(prev => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  }
  function updateProposalField(idx, key, val) {
    setIngestProposals(prev => prev.map((p, i) => i === idx ? { ...p, fields: { ...p.fields, [key]: val } } : p));
  }

  useEffect(() => { document.title = 'Arx Acta'; fetchAssets(); }, []);

  async function fetchAssets() {
    setLoading(true);
    try {
      const { data } = await api.get('/arx/assets');
      setAssets(data);
      const counts = { all: data.length };
      TABS.slice(1).forEach(t => { counts[t] = data.filter(a => a.category === t).length; });
      setTabCounts(counts);
    } catch {}
    finally { setLoading(false); }
  }

  async function fetchAsset(id) {
    setAssetLoading(true);
    try {
      const { data } = await api.get(`/arx/assets/${id}`);
      setSelectedAsset(data);
    } catch {}
    finally { setAssetLoading(false); }
  }

  function selectAsset(id) {
    setSelectedId(id);
    fetchAsset(id);
    setExtractedFields(null);
    if (window.innerWidth < 768) setMobileView('detail');
  }

  const filteredAssets = activeTab === 'all' ? assets : assets.filter(a => a.category === activeTab);

  async function createAsset() {
    if (!newAssetName.trim()) return;
    setCreating(true);
    try {
      const tags = newAssetTags.split(',').map(t => t.trim()).filter(Boolean);
      const { data } = await api.post('/arx/assets', { name: newAssetName.trim(), category: newAssetCategory, tags });
      setAssets(prev => [...prev, data]);
      setTabCounts(prev => ({ ...prev, all: (prev.all || 0) + 1, [newAssetCategory]: (prev[newAssetCategory] || 0) + 1 }));
      setShowNewAsset(false);
      setNewAssetName(''); setNewAssetCategory('person'); setNewAssetTags('');
      selectAsset(data.id);
      feedback.confirm();
    } catch { feedback.fail(); }
    finally { setCreating(false); }
  }

  async function deleteAsset(id) {
    if (!window.confirm('Delete this asset and all its documents?')) return;
    const snapshot = [...assets];
    setAssets(prev => prev.filter(a => a.id !== id));
    setTabCounts(prev => {
      const cat = assets.find(a => a.id === id)?.category;
      return {
        ...prev,
        all: Math.max(0, (prev.all || 0) - 1),
        ...(cat ? { [cat]: Math.max(0, (prev[cat] || 0) - 1) } : {}),
      };
    });
    if (selectedId === id) { setSelectedId(null); setSelectedAsset(null); setMobileView('list'); }
    try {
      await api.delete(`/arx/assets/${id}`);
      feedback.confirm();
    } catch {
      setAssets(snapshot);
      feedback.fail();
    }
  }

  async function saveField(fieldKey, value) {
    if (!selectedAsset) return;
    const updatedFields = { ...selectedAsset.fields, [fieldKey]: value };
    try {
      const { data } = await api.put(`/arx/assets/${selectedAsset.id}`, { fields: updatedFields });
      setSelectedAsset(prev => ({ ...prev, fields: data.fields }));
    } catch { feedback.fail(); }
  }

  async function deleteDocument(docId) {
    if (!selectedAsset) return;
    try {
      await api.delete(`/arx/assets/${selectedAsset.id}/documents/${docId}`);
      setSelectedAsset(prev => ({ ...prev, documents: prev.documents.filter(d => d.id !== docId) }));
      feedback.confirm();
    } catch { feedback.fail(); }
  }

  async function runExtract(docId) {
    setExtractDocId(docId);
    setExtracting(true);
    setExtractedFields(null);
    try {
      const { data } = await api.post(`/arx/assets/${selectedAsset.id}/extract`, { doc_id: docId });
      setExtractedFields(data.extracted_fields || {});
      const allSelected = {};
      Object.keys(data.extracted_fields || {}).forEach(k => { allSelected[k] = true; });
      setSelectedExtractFields(allSelected);
    } catch { feedback.fail(); }
    finally { setExtracting(false); }
  }

  async function applyExtracted() {
    if (!extractedFields || !selectedAsset) return;
    const toApply = {};
    Object.entries(extractedFields).forEach(([k, v]) => { if (selectedExtractFields[k]) toApply[k] = v; });
    try {
      const { data } = await api.put(`/arx/assets/${selectedAsset.id}/documents/${extractDocId}/apply`, { fields: toApply });
      setSelectedAsset(prev => ({ ...prev, fields: data.fields }));
      setExtractedFields(null);
      feedback.confirm();
    } catch { feedback.fail(); }
  }

  async function handleComposerSubmit() {
    const hasFiles = composerFiles.length > 0;
    const hasText = composerText.trim().length > 0;
    if (!hasFiles && !hasText) return;
    setComposerSending(true);
    try {
      // Route 1: files + asset selected -> direct attach + auto-extract
      if (hasFiles && selectedAsset) {
        for (const f of composerFiles) {
          const form = new FormData();
          form.append('file', f.file);
          const { data } = await api.post(`/arx/assets/${selectedAsset.id}/documents`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
          try {
            const { data: extracted } = await api.post(`/arx/assets/${selectedAsset.id}/extract`, { doc_id: data.id });
            if (extracted.extracted_fields && Object.keys(extracted.extracted_fields).length > 0) {
              setExtractedFields(extracted.extracted_fields);
              setSelectedExtractFields(Object.fromEntries(Object.keys(extracted.extracted_fields).map(k => [k, true])));
            }
          } catch { /* extraction optional */ }
        }
        await fetchAsset(selectedAsset.id);
        feedback.confirm();
        closeComposer();
        return;
      }
      // Route 2: files + no asset selected -> ingest with classification
      if (hasFiles && !selectedAsset) {
        const form = new FormData();
        form.append('text', composerText.trim());
        composerFiles.forEach(f => form.append('files', f.file));
        const { data } = await api.post('/arx/ingest', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        setIngestProposals(data.proposals || []);
        setIngestStep('review');
        setShowIngestion(true);
        closeComposer();
        return;
      }
      // Text only: in-app Arx Q&A was retired (it sent raw records to a
      // model). Ask Claude, which reads sanitised Arx over MCP.
      if (hasText && !hasFiles) {
        feedback.fail('Ask Claude for questions about Arx records');
        return;
      }
    } catch { feedback.fail(); }
    finally { setComposerSending(false); }
  }

  async function fillPdf() {
    if (!pdfFile || pdfFillAssets.length === 0) return;
    setPdfFilling(true);
    // First upload the PDF to Cloudinary via backend upload endpoint
    try {
      const form = new FormData();
      form.append('file', pdfFile);
      const { data: uploadData } = await api.post('/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const pdfUrl = uploadData.url;
      const { data } = await api.post('/arx/fill-pdf', {
        pdf_url: pdfUrl,
        asset_ids: pdfFillAssets,
        instructions: pdfFillInstructions
      });
      setFilledPdfUrl(data.filled_pdf_url);
      feedback.confirm();
    } catch { feedback.fail(); }
    finally { setPdfFilling(false); }
  }

  async function toggleLinkedAsset(assetId) {
    if (!selectedAsset) return;
    const current = selectedAsset.linked_asset_ids || [];
    const updated = current.includes(assetId) ? current.filter(id => id !== assetId) : [...current, assetId];
    try {
      const { data } = await api.put(`/arx/assets/${selectedAsset.id}`, { linked_asset_ids: updated });
      setSelectedAsset(prev => ({ ...prev, linked_asset_ids: data.linked_asset_ids }));
    } catch { feedback.fail(); }
  }

  const fields = CATEGORY_FIELDS[selectedAsset?.category] || [];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=JetBrains+Mono:wght@300;400&family=DM+Sans:wght@300;400&display=swap');
        .arx-page { background: ${BG}; min-height: 100vh; color: ${TEXT}; font-family: 'Inter', system-ui, sans-serif; }
        .arx-scrollbar::-webkit-scrollbar { width: 4px; }
        .arx-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .arx-scrollbar::-webkit-scrollbar-thumb { background: #333; border-radius: 2px; }
        .arx-tab:hover { color: ${TEXT} !important; }
        .arx-asset-row:hover { background: rgba(255,255,255,0.05) !important; }
        .arx-field-row:hover { background: rgba(255,255,255,0.05); }
      `}</style>

      <div className="arx-page">
        {/* Header */}
        <div style={{ padding: '28px 32px 20px', borderBottom: `1px solid ${BORDER}` }}>
          <div className="flex items-center mb-1" style={{ gap: 2 }}>
            <Logotype app="arx" size="header" />
          </div>
          <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: TEXT2, letterSpacing: '0.18em', paddingLeft: 42 }}>The Archive of Life</p>
        </div>

        {/* Category tabs — full width under header */}
        <ZTabs
          tabs={TABS.map(t => ({ id: t, label: t === 'all' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1) }))}
          activeTab={activeTab}
          onChange={setActiveTab}
          accent={ACCENT}
        />

        <div style={{ display: 'flex', height: 'calc(100vh - 150px)' }}>

          {/* Left Panel — Asset List */}
          <div style={{ width: isMobile ? '100%' : 320, flexShrink: 0, borderRight: isMobile ? 'none' : `1px solid ${BORDER}`, display: isMobile && mobileView === 'detail' ? 'none' : 'flex', flexDirection: 'column', position: 'relative' }}>

            {/* Arx queue banner — first row of content area per Section 10.x */}
            {queueCount.by_app?.arx > 0 && (
              <div style={{
                background: 'rgba(232,197,71,0.06)',
                border: '0.5px solid rgba(232,197,71,0.2)',
                borderRadius: 10,
                padding: '10px 14px',
                margin: '12px 16px 0',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#E8C547', flexShrink: 0 }} />
                <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#b0b4c0', margin: 0, flex: 1 }}>
                  {queueCount.by_app.arx} item{queueCount.by_app.arx !== 1 ? 's' : ''} waiting for review
                </p>
                <button
                  onClick={() => openQueue('arx')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#E8C547', padding: 0 }}
                >
                  Review
                </button>
              </div>
            )}

            {/* Asset list */}
            <div className="arx-scrollbar" style={{ flex: 1, overflowY: 'auto' }}>
              {loading ? (
                <div style={{ padding: 24 }}><ZLoadingBar accent={ACCENT} loading={true} /></div>
              ) : filteredAssets.length === 0 ? (
                <ZEmptyState mark="A" accent={ACCENT} message="No assets" />
              ) : filteredAssets.map(asset => (
                <div
                  key={asset.id}
                  className="arx-asset-row"
                  onClick={() => selectAsset(asset.id)}
                  style={{
                    padding: '12px 16px', cursor: 'pointer', transition: 'background 0.15s',
                    background: selectedId === asset.id ? 'rgba(255,255,255,0.05)' : 'transparent',
                    borderLeft: selectedId === asset.id ? `3px solid ${ACCENT}` : '3px solid transparent',
                    borderBottom: `0.5px solid ${BORDER}`,
                    position: 'relative',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 400, fontSize: 14, color: TEXT }}>{asset.name}</span>
                    {asset.document_count > 0 && (
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: TEXT3 }}>{asset.document_count}</span>
                    )}
                  </div>
                  <div style={{ marginTop: 4 }}><CategoryPill cat={asset.category} /></div>
                  <div onClick={e => e.stopPropagation()}>
                    <OverflowMenu
                      items={[
                        { icon: <Pencil size={16} strokeWidth={1.5} />, label: 'Edit', onClick: () => selectAsset(asset.id) },
                        { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete', onClick: () => deleteAsset(asset.id), destructive: true },
                      ]}
                    />
                  </div>
                </div>
              ))}
            </div>



          </div>

          {/* Right Panel — Asset Profile */}
          <div className="arx-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: 0, display: isMobile && mobileView === 'list' ? 'none' : 'block' }}>
            {!selectedAsset ? (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: TEXT3, fontSize: 14, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.1em' }}>
                Select an asset to view its profile
              </div>
            ) : assetLoading ? (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Loader2 size={22} style={{ animation: 'spin 1s linear infinite', color: ACCENT }} />
              </div>
            ) : (
              <div>
                {/* Asset header */}
                <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {isMobile && (
                      <button
                        onClick={() => setMobileView('list')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEXT2, padding: '0 4px 0 0', display: 'flex', alignItems: 'center' }}
                      >
                        <ChevronLeft size={22} />
                      </button>
                    )}
                    <div>
                      <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 300, margin: 0, lineHeight: 1.1 }}>{selectedAsset.name}</h2>
                      <div style={{ marginTop: 6 }}><CategoryPill cat={selectedAsset.category} /></div>
                    </div>
                  </div>
                  <OverflowMenu
                    items={[
                      { icon: <Pencil size={16} strokeWidth={1.5} />, label: 'Edit', onClick: () => {} },
                      { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete', onClick: () => deleteAsset(selectedAsset.id), destructive: true },
                    ]}
                  />
                </div>

                {/* Fields */}
                <div style={{ padding: '16px 24px' }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: TEXT3, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 12 }}>Fields</div>
                  {fields.map(f => (
                    <FieldRow
                      key={f}
                      fieldKey={f}
                      value={selectedAsset.fields?.[f] || ''}
                      onSave={saveField}
                    />
                  ))}
                </div>

                {/* AI Extracted fields review */}
                {extractedFields && (
                  <div style={{ margin: '0 24px 20px', background: 'rgba(255,255,255,0.05)', border: `1px solid ${ACCENT}44`, borderRadius: 10, padding: 16 }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: ACCENT, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 12 }}>
                      AI Extracted — Review &amp; Apply
                    </div>
                    {Object.keys(extractedFields).length === 0 ? (
                      <p style={{ fontSize: 13, color: TEXT3 }}>No fields extracted from this document.</p>
                    ) : (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 12 }}>
                          {Object.entries(extractedFields).map(([k, v]) => (
                            <div key={k} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', background: '#111', borderRadius: 6 }}>
                              <input
                                type="checkbox"
                                checked={!!selectedExtractFields[k]}
                                onChange={e => setSelectedExtractFields(prev => ({ ...prev, [k]: e.target.checked }))}
                                style={{ marginTop: 3, accentColor: ACCENT, flexShrink: 0 }}
                              />
                              <div>
                                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: TEXT2, marginBottom: 2 }}>{fieldLabel(k)}</div>
                                <div style={{ fontSize: 13, color: TEXT }}>{v}</div>
                                {selectedAsset.fields?.[k] && selectedAsset.fields[k] !== v && (
                                  <div style={{ fontSize: 11, color: TEXT3, marginTop: 2 }}>Current: {selectedAsset.fields[k]}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={applyExtracted} style={{ background: ACCENT, border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', color: '#000', fontSize: 13 }}>
                            Apply Selected
                          </button>
                          <button onClick={() => setExtractedFields(null)} style={{ background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '8px 12px', cursor: 'pointer', color: TEXT2, fontSize: 13 }}>
                            Dismiss
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Documents */}
                <div style={{ padding: '0 24px 20px' }}>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: TEXT3, letterSpacing: '0.18em', textTransform: 'uppercase' }}>Documents</div>
                  </div>
                  {(!selectedAsset.documents || selectedAsset.documents.length === 0) ? (
                    <p style={{ fontSize: 13, color: TEXT3, fontStyle: 'italic' }}>No documents attached</p>
                  ) : selectedAsset.documents.map(doc => (
                    <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: CARD, borderRadius: 8, border: `1px solid ${BORDER}`, marginBottom: 6, position: 'relative' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: TEXT, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{doc.filename}</div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'center' }}>
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: TEXT3, background: '#222', padding: '2px 6px', borderRadius: 3 }}>{doc.file_type}</span>
                          <span style={{ fontSize: 10, color: TEXT3 }}>{doc.uploaded_at?.slice(0, 10)}</span>
                        </div>
                      </div>
                      {extracting && extractDocId === doc.id && (
                        <Loader2 size={12} style={{ animation: 'spin 1s linear infinite', color: ACCENT, flexShrink: 0 }} />
                      )}
                      <OverflowMenu
                        items={[
                          { icon: <Sparkles size={16} strokeWidth={1.5} />, label: 'Extract with AI', onClick: () => runExtract(doc.id) },
                          { icon: <Download size={16} strokeWidth={1.5} />, label: 'Download', onClick: () => window.open(doc.file_url, '_blank') },
                          { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete', onClick: () => deleteDocument(doc.id), destructive: true },
                        ]}
                      />
                    </div>
                  ))}
                </div>

                {/* Linked Assets */}
                <div style={{ padding: '0 24px 32px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: TEXT3, letterSpacing: '0.18em', textTransform: 'uppercase' }}>Linked Assets</div>
                    <button onClick={() => setShowLinkPicker(true)} style={{ background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '5px 10px', cursor: 'pointer', color: TEXT2, display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
                      <LinkIcon size={13} /> Link
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {(selectedAsset.linked_asset_ids || []).length === 0 ? (
                      <span style={{ fontSize: 13, color: TEXT3, fontStyle: 'italic' }}>No linked assets</span>
                    ) : (selectedAsset.linked_asset_ids || []).map(lid => {
                      const linked = assets.find(a => a.id === lid);
                      if (!linked) return null;
                      return (
                        <button key={lid} onClick={() => selectAsset(lid)} style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}33`, borderRadius: 20, padding: '5px 12px', cursor: 'pointer', color: ACCENT, fontSize: 12 }}>
                          {linked.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Floating Composer ─────────────────────────────────────────────────── */}

      {/* Trigger bar — collapsed state */}
      {!composerOpen && (
        <div className="fixed bottom-[72px] md:bottom-8 left-0 md:left-64 right-0 z-30 pointer-events-none flex justify-center">
          <div className="w-[70%] max-w-xl">
            <div
              onDragOver={e => { e.preventDefault(); setComposerDragging(true); }}
              onDragLeave={() => setComposerDragging(false)}
              onDrop={e => { e.preventDefault(); setComposerDragging(false); addFiles(e.dataTransfer.files, setComposerFiles); openComposer(); }}
              onClick={openComposer}
              className="pointer-events-auto w-full flex items-center gap-3 border-2 rounded-xl px-4 py-3 shadow-xl active:scale-[0.99] transition-all backdrop-blur-xl cursor-pointer"
              style={{
                background: 'rgba(10, 10, 15, 0.80)',
                borderColor: composerDragging ? '#9B6DD4' : `${ACCENT}cc`,
              }}
              onMouseEnter={e => { if (!composerDragging) e.currentTarget.style.borderColor = ACCENT; }}
              onMouseLeave={e => { if (!composerDragging) e.currentTarget.style.borderColor = `${ACCENT}cc`; }}
            >
              <span className="flex-1 text-sm text-white font-medium">
                {composerDragging ? 'Drop to archive...' : 'Archive anything...'}
              </span>
              <Upload size={18} className="text-white/40 shrink-0" />
            </div>
          </div>
        </div>
      )}

      {/* Expanded composer */}
      {composerOpen && (
        <>
          <div className="fixed inset-0 z-[55]" onClick={closeComposer} />
          <div
            className="fixed left-0 md:left-64 right-0 z-[60] pb-2"
            style={{ bottom: composerKeyboardOffset }}
            onClick={e => e.stopPropagation()}
            onDragOver={e => { e.preventDefault(); setComposerDragging(true); }}
            onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setComposerDragging(false); }}
            onDrop={e => { e.preventDefault(); setComposerDragging(false); addFiles(e.dataTransfer.files, setComposerFiles); }}
          >
            <div className="w-[70%] max-w-xl mx-auto">
              <div
                className="w-full border-2 rounded-2xl shadow-2xl overflow-hidden transition-colors backdrop-blur-xl"
                style={{
                  background: 'rgba(10, 10, 15, 0.92)',
                  borderColor: composerDragging ? '#9B6DD4' : `${ACCENT}cc`,
                }}
              >
                {/* Header */}
                <div className="flex items-center gap-2 px-4 pt-3 pb-1">
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: TEXT2, letterSpacing: '0.18em', textTransform: 'uppercase', flex: 1 }}>Arx Acta</span>
                  <button onClick={closeComposer} className="p-1 text-white/30 hover:text-white/70 transition-colors">
                    <X size={16} />
                  </button>
                </div>

                {/* Textarea */}
                <div className="px-4 pb-2">
                  <textarea
                    ref={composerInputRef}
                    autoFocus
                    value={composerText}
                    rows={1}
                    onChange={e => {
                      setComposerText(e.target.value);
                      e.target.style.height = 'auto';
                      const maxPx = Math.round(window.innerHeight * 0.30);
                      e.target.style.height = Math.min(e.target.scrollHeight, maxPx) + 'px';
                    }}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComposerSubmit(); } }}
                    onPaste={e => {
                      const items = e.clipboardData?.items;
                      if (!items) return;
                      const pastedFiles = [];
                      for (let i = 0; i < items.length; i++) {
                        if (items[i].kind === 'file') { const f = items[i].getAsFile(); if (f) pastedFiles.push(f); }
                      }
                      if (pastedFiles.length) { e.preventDefault(); addFiles(pastedFiles, setComposerFiles); feedback.confirm(); }
                    }}
                    placeholder={selectedAsset
                      ? `Attach files to ${selectedAsset.name}...`
                      : 'Drop documents to file a new record...'
                    }
                    disabled={composerSending}
                    className="w-full bg-transparent text-[15px] text-[#f0f0f5] placeholder-white/25 focus:outline-none font-light resize-none"
                    style={{ minHeight: 28, maxHeight: '30vh', overflowY: 'auto', fontFamily: 'inherit' }}
                  />
                </div>

                {/* File chips */}
                {composerFiles.length > 0 && (
                  <div className="px-4 pb-2 flex flex-wrap gap-2">
                    {composerFiles.map((f, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: '5px 10px', maxWidth: 200 }}>
                        {f.preview ? (
                          <img src={f.preview} alt="" style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} />
                        ) : (
                          <FileText size={14} style={{ color: TEXT2, flexShrink: 0 }} />
                        )}
                        <span style={{ fontSize: 11, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                        <button onClick={() => setComposerFiles(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEXT3, padding: 0, flexShrink: 0 }}>
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Action row */}
                <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: `1px solid ${BORDER}` }}>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-1.5 text-white/55 hover:text-white transition-colors cursor-pointer">
                      <Upload size={18} />
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>Attach</span>
                      <input ref={composerFileRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.txt" className="hidden" onChange={e => { if (e.target.files?.length) addFiles(e.target.files, setComposerFiles); e.target.value = ''; }} />
                    </label>
                    <button
                      onClick={() => { closeComposer(); setPdfFile(null); setShowPdfFill(true); setFilledPdfUrl(null); }}
                      className="flex items-center gap-1.5 text-white/55 hover:text-white transition-colors"
                    >
                      <Paperclip size={18} />
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>Fill PDF</span>
                    </button>
                  </div>
                  {(composerText.trim() || composerFiles.length > 0) ? (
                    <button
                      onClick={handleComposerSubmit}
                      disabled={composerSending}
                      className="flex items-center gap-2 px-4 py-2 text-black rounded-full text-sm transition-colors"
                      style={{ background: ACCENT, fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}
                    >
                      {composerSending ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : 'Send'}
                    </button>
                  ) : (
                    <button onClick={closeComposer} className="text-xs text-white/30 hover:text-white/60 px-2 py-1 transition-colors" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Cancel</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Ingestion Modal */}
      {showIngestion && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}
        >
          <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BORDER}`, borderRadius: 14, width: '100%', maxWidth: 520, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* ── Step: processing ────────────────────────────────── */}
            {ingestStep === 'processing' && (
              <div style={{ padding: 56, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
                <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: ACCENT }} />
                <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: TEXT2, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                  Zephyr is reading {ingestFiles.length > 0 ? `${ingestFiles.length} document${ingestFiles.length > 1 ? 's' : ''}` : 'your input'}...
                </p>
              </div>
            )}

            {/* ── Step: review ────────────────────────────────────── */}
            {ingestStep === 'review' && (<>
              <div style={{ padding: '18px 22px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: TEXT2, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
                  {ingestProposals.filter(p => !p._discard).length} proposal{ingestProposals.filter(p => !p._discard).length !== 1 ? 's' : ''} — review &amp; edit
                </div>
                <button onClick={closeIngestion} style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEXT3 }}><X size={15} /></button>
              </div>

              {/* Proposal cards */}
              <div className="arx-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '0 22px' }}>
                {ingestProposals.map((p, idx) => !p._discard && (
                  <div key={idx} style={{ background: '#0F0F0F', border: `1px solid ${BORDER}`, borderRadius: 10, padding: 14, marginBottom: 10, position: 'relative' }}>
                    {/* Discard this proposal */}
                    <button onClick={() => updateProposal(idx, { _discard: true })} style={{ position: 'absolute', top: 10, right: 10, background: 'none', border: 'none', cursor: 'pointer', color: TEXT3 }}>
                      <X size={13} />
                    </button>

                    {/* Doc filename */}
                    {p.doc_filename && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                        <FileText size={12} style={{ color: TEXT3, flexShrink: 0 }} />
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: TEXT3 }}>{p.doc_filename}</span>
                      </div>
                    )}

                    {/* Action badge + asset picker */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                      <button
                        onClick={() => updateProposal(idx, { action: p.action === 'create_new' ? 'add_to_existing' : 'create_new', asset_id: null })}
                        style={{ padding: '3px 9px', borderRadius: 5, fontSize: 10, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.08em', border: 'none', cursor: 'pointer', background: p.action === 'create_new' ? `${ACCENT}22` : '#10B98122', color: p.action === 'create_new' ? ACCENT : '#10B981' }}
                      >
                        {p.action === 'create_new' ? '+ New Asset' : '→ Existing'}
                      </button>
                      {p.action === 'add_to_existing' && (
                        <select
                          value={p.asset_id || ''}
                          onChange={e => {
                            const a = assets.find(a => a.id === e.target.value);
                            updateProposal(idx, { asset_id: e.target.value, asset_name: a?.name || p.asset_name, category: a?.category || p.category });
                          }}
                          style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '3px 8px', color: TEXT, fontSize: 12, outline: 'none', maxWidth: 200 }}
                        >
                          <option value="">Select asset…</option>
                          {assets.map(a => <option key={a.id} value={a.id}>{a.name} ({a.category})</option>)}
                        </select>
                      )}
                    </div>

                    {/* Name + Category row */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
                      <input
                        value={p.asset_name || ''}
                        onChange={e => updateProposal(idx, { asset_name: e.target.value })}
                        placeholder="Asset name"
                        style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 7, padding: '7px 10px', color: TEXT, fontSize: 14, fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, letterSpacing: '0.04em', outline: 'none' }}
                      />
                      <select
                        value={p.category || 'person'}
                        onChange={e => updateProposal(idx, { category: e.target.value })}
                        style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 7, padding: '7px 10px', color: TEXT, fontSize: 12, outline: 'none', flexShrink: 0 }}
                      >
                        <option value="person">Person</option>
                        <option value="business">Business</option>
                        <option value="physical">Physical</option>
                        <option value="legal">Legal</option>
                        <option value="credential">Credential</option>
                      </select>
                    </div>

                    {/* Explanation */}
                    {p.explanation && (
                      <p style={{ fontSize: 11, color: TEXT3, fontStyle: 'italic', marginBottom: 10, lineHeight: 1.5 }}>{p.explanation}</p>
                    )}

                    {/* Extracted fields — editable */}
                    {Object.keys(p.fields || {}).length > 0 && (
                      <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 7, padding: '8px 10px' }}>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: TEXT3, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 8 }}>Extracted Fields</div>
                        {Object.entries(p.fields).map(([k, v]) => (
                          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <span style={{ width: 150, fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: TEXT3, flexShrink: 0 }}>{fieldLabel(k)}</span>
                            <input
                              value={v || ''}
                              onChange={e => updateProposalField(idx, k, e.target.value)}
                              style={{ flex: 1, background: '#0F0F0F', border: `1px solid ${BORDER}`, borderRadius: 5, padding: '4px 8px', color: TEXT, fontSize: 12, outline: 'none', fontFamily: 'inherit' }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {ingestProposals.every(p => p._discard) && (
                  <div style={{ textAlign: 'center', padding: '20px 0', color: TEXT3, fontSize: 13, fontStyle: 'italic' }}>All proposals discarded</div>
                )}
              </div>

              {/* Accept / Discard */}
              <div style={{ padding: '14px 22px 20px', display: 'flex', gap: 10, flexShrink: 0, borderTop: `1px solid ${BORDER}` }}>
                <button
                  onClick={acceptIngest}
                  disabled={ingestApplying || ingestProposals.every(p => p._discard)}
                  style={{ flex: 1, padding: '10px', background: ACCENT, border: 'none', borderRadius: 8, cursor: 'pointer', color: '#000', fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, opacity: (ingestApplying || ingestProposals.every(p => p._discard)) ? 0.5 : 1 }}
                >
                  {ingestApplying ? <><Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</> : <><Check size={15} /> Accept {ingestProposals.filter(p => !p._discard).length}</>}
                </button>
                <button
                  onClick={closeIngestion}
                  style={{ padding: '10px 18px', background: 'none', border: `1px solid ${BORDER}`, borderRadius: 8, cursor: 'pointer', color: TEXT2, fontSize: 14 }}
                >
                  Discard
                </button>
              </div>
            </>)}

          </div>
        </div>
      )}

      {/* New Asset Modal */}
      {showNewAsset && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
          <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BORDER}`, borderRadius: 12, padding: 28, width: '100%', maxWidth: 420 }}>
            <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 300, margin: '0 0 20px', color: TEXT }}>New Asset</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 11, color: TEXT2, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.08em' }}>Name</label>
                <input
                  value={newAssetName} onChange={e => setNewAssetName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && createAsset()}
                  autoFocus
                  placeholder="Werner du Toit"
                  style={{ width: '100%', marginTop: 6, background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 7, padding: '10px 12px', color: TEXT, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, color: TEXT2, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.08em' }}>Category</label>
                <select
                  value={newAssetCategory} onChange={e => setNewAssetCategory(e.target.value)}
                  style={{ width: '100%', marginTop: 6, background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 7, padding: '10px 12px', color: TEXT, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                >
                  <option value="person">Person</option>
                  <option value="business">Business</option>
                  <option value="physical">Physical Asset</option>
                  <option value="legal">Legal</option>
                  <option value="credential">Credential</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, color: TEXT2, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.08em' }}>Tags (comma-separated)</label>
                <input
                  value={newAssetTags} onChange={e => setNewAssetTags(e.target.value)}
                  placeholder="owner, family"
                  style={{ width: '100%', marginTop: 6, background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 7, padding: '10px 12px', color: TEXT, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
                <button onClick={() => { setShowNewAsset(false); setNewAssetName(''); setNewAssetTags(''); }} style={{ background: 'none', border: `1px solid ${BORDER}`, borderRadius: 7, padding: '9px 18px', cursor: 'pointer', color: TEXT2, fontSize: 14 }}>Cancel</button>
                <button onClick={createAsset} disabled={creating || !newAssetName.trim()} style={{ background: ACCENT, border: 'none', borderRadius: 7, padding: '9px 20px', cursor: 'pointer', color: '#000', fontSize: 14, fontWeight: 500, opacity: creating ? 0.6 : 1 }}>
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Fill Modal */}
      {showPdfFill && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
          <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BORDER}`, borderRadius: 12, padding: 28, width: '100%', maxWidth: 480 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 300, margin: 0 }}>Fill PDF Form</h3>
              <button onClick={() => setShowPdfFill(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEXT2 }}><X size={18} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 11, color: TEXT2, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.08em' }}>PDF Form</label>
                <div style={{ marginTop: 6 }}>
                  <button onClick={() => pdfInputRef.current?.click()} style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 7, padding: '9px 14px', cursor: 'pointer', color: pdfFile ? TEXT : TEXT3, fontSize: 13, width: '100%', textAlign: 'left' }}>
                    {pdfFile ? pdfFile.name : 'Choose PDF...'}
                  </button>
                  <input ref={pdfInputRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={e => { setPdfFile(e.target.files?.[0] || null); e.target.value = ''; }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, color: TEXT2, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.08em' }}>Use data from</label>
                <div style={{ marginTop: 8, maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {assets.map(a => (
                    <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: pdfFillAssets.includes(a.id) ? `${ACCENT}11` : 'transparent', borderRadius: 6, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={pdfFillAssets.includes(a.id)}
                        onChange={e => setPdfFillAssets(prev => e.target.checked ? [...prev, a.id] : prev.filter(id => id !== a.id))}
                        style={{ accentColor: ACCENT }}
                      />
                      <span style={{ fontSize: 13, color: TEXT }}>{a.name}</span>
                      <CategoryPill cat={a.category} />
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, color: TEXT2, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.08em' }}>Instructions (optional)</label>
                <input
                  value={pdfFillInstructions} onChange={e => setPdfFillInstructions(e.target.value)}
                  placeholder="Fill in the VAT return for KayaX"
                  style={{ width: '100%', marginTop: 6, background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 7, padding: '10px 12px', color: TEXT, fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                />
              </div>
              {filledPdfUrl ? (
                <a href={filledPdfUrl} target="_blank" rel="noopener noreferrer" style={{ background: ACCENT, border: 'none', borderRadius: 7, padding: '10px', cursor: 'pointer', color: '#000', fontSize: 14, fontWeight: 500, textAlign: 'center', textDecoration: 'none', display: 'block' }}>
                  Download Filled PDF
                </a>
              ) : (
                <button onClick={fillPdf} disabled={pdfFilling || !pdfFile || pdfFillAssets.length === 0} style={{ background: ACCENT, border: 'none', borderRadius: 7, padding: '10px', cursor: 'pointer', color: '#000', fontSize: 14, fontWeight: 500, opacity: (pdfFilling || !pdfFile || pdfFillAssets.length === 0) ? 0.5 : 1 }}>
                  {pdfFilling ? 'Filling...' : 'Fill PDF'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Link Picker Modal */}
      {showLinkPicker && selectedAsset && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
          <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BORDER}`, borderRadius: 12, padding: 28, width: '100%', maxWidth: 400, maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 300, margin: 0 }}>Link Assets</h3>
              <button onClick={() => setShowLinkPicker(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEXT2 }}><X size={16} /></button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {assets.filter(a => a.id !== selectedAsset.id).map(a => {
                const linked = (selectedAsset.linked_asset_ids || []).includes(a.id);
                return (
                  <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', background: linked ? `${ACCENT}11` : 'transparent', borderRadius: 6, cursor: 'pointer', marginBottom: 4 }}>
                    <input type="checkbox" checked={linked} onChange={() => toggleLinkedAsset(a.id)} style={{ accentColor: ACCENT }} />
                    <span style={{ fontSize: 13, color: TEXT, flex: 1 }}>{a.name}</span>
                    <CategoryPill cat={a.category} />
                  </label>
                );
              })}
            </div>
            <button onClick={() => setShowLinkPicker(false)} style={{ marginTop: 16, background: ACCENT, border: 'none', borderRadius: 7, padding: '9px', cursor: 'pointer', color: '#000', fontSize: 14, fontWeight: 500 }}>Done</button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}
